import { useMemo } from 'react';
import { useCACalculations } from './useCACalculations';
import { useChargesCalculations } from './useChargesCalculations';
import { useQuotePartCalculations } from './useQuotePartCalculations';
import { useConsultantCalculations } from './useConsultantCalculations';
import { useFacturesCalculations } from './useFacturesCalculations';
import { useRefacturationCalculations } from './useRefacturationCalculations';

/**
 * Hook principal qui orchestre tous les calculs P&L
 */
export function usePLCalculations(charges, consultantConfig, tempsData, facturesInternes, filterAnnee) {
  // 1. CA (consultants issus des temps)
  const { caTotal, caByConsultant, consultants: tempsConsultants } = useCACalculations(tempsData);

  // 1b. Fusionner les consultants config + temps
  const configConsultantNames = useMemo(() => {
    const entry = consultantConfig[filterAnnee];
    if (entry && typeof entry === 'object' && entry.consultantNames) {
      return entry.consultantNames;
    }
    return [];
  }, [consultantConfig, filterAnnee]);

  const allConsultants = useMemo(() => {
    const set = new Set([...tempsConsultants, ...configConsultantNames]);
    return [...set].sort();
  }, [tempsConsultants, configConsultantNames]);

  // 2. Charges
  const { chargesByType, totalCharges } = useChargesCalculations(charges);

  // 3. Marge brute
  const margeBrute = useMemo(() => ({
    Q1: caTotal.Q1 - totalCharges.Q1,
    Q2: caTotal.Q2 - totalCharges.Q2,
    Q3: caTotal.Q3 - totalCharges.Q3,
    Q4: caTotal.Q4 - totalCharges.Q4,
    ANNUEL: caTotal.ANNUEL - totalCharges.ANNUEL
  }), [caTotal, totalCharges]);

  // 4. Quote-part (avec liste fusionnée de consultants)
  const quotePart = useQuotePartCalculations(charges, consultantConfig, filterAnnee, allConsultants);

  // 5. Refacturation (prend en compte tmpexcl)
  const { refacturationByConsultant: rawRefacturation, tmpByQuarter } = useRefacturationCalculations(tempsData);

  // 5b. Enrichir la refacturation avec des entrées zéro pour les consultants config-only
  const refacturationByConsultant = useMemo(() => {
    const result = { ...rawRefacturation };
    allConsultants.forEach(name => {
      if (!result[name]) {
        result[name] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, ANNUEL: 0 };
      }
    });
    return result;
  }, [rawRefacturation, allConsultants]);

  // 6. CA net (basé sur la refacturation enrichie)
  const caNet = useConsultantCalculations(refacturationByConsultant, quotePart);

  // 7. Factures + Reste
  const { factures, reste } = useFacturesCalculations(facturesInternes, caNet);

  return {
    ca: caTotal,
    chargesByType,
    totalCharges,
    margeBrute,
    quotePart,
    caByConsultant,
    refacturationByConsultant,
    tmpByQuarter,
    caNet,
    factures,
    reste,
    consultants: allConsultants
  };
}
